import { useEffect, useState, useCallback } from "react";
import { Link, NavLink, Navigate, useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import AdminPaymentsTab from "./AdminPayments";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from "recharts";

function flag(cc) {
  if (!cc || cc === "??" || cc.length !== 2) return "🌐";
  const codePoints = [...cc.toUpperCase()].map((c) => 0x1F1E6 - 65 + c.charCodeAt(0));
  return String.fromCodePoint(...codePoints);
}

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 30) return "ahora";
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
    });
  } catch { return iso; }
}

function StatCard({ label, value, color, testId }) {
  return (
    <div
      className="border-2 border-[#1C1917] bg-white p-5 shadow-[3px_3px_0_0_#1C1917]"
      data-testid={testId}
    >
      <span className="eyebrow text-xs">{label}</span>
      <div className="display text-4xl mt-1" style={{ color: color || "#1C1917" }}>
        {value}
      </div>
    </div>
  );
}

function TrafficChart() {
  const [data, setData] = useState(null);
  const [hours, setHours] = useState(24);

  useEffect(() => {
    api.get(`/admin/timeseries?hours=${hours}`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [hours]);

  return (
    <div className="border-2 border-[#1C1917] bg-white p-5 shadow-[3px_3px_0_0_#1C1917]" data-testid="traffic-chart">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <span className="eyebrow text-xs">Tráfico {hours === 24 ? "últimas 24h" : "últimos 7 días"}</span>
        <div className="inline-flex border-2 border-[#1C1917]">
          {[
            { v: 24, label: "24 h" },
            { v: 168, label: "7 días" },
          ].map((opt, i) => (
            <button
              key={opt.v}
              onClick={() => setHours(opt.v)}
              className={`px-3 h-8 text-[10px] font-black tracking-widest uppercase ${i > 0 ? "border-l-2 border-[#1C1917]" : ""} ${
                hours === opt.v ? "bg-[#1C1917] text-[#FDFBF7]" : "bg-white text-[#1C1917] hover:bg-[#FFC53D]"
              }`}
              data-testid={`chart-range-${opt.v}`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>
      <div style={{ width: "100%", height: 280 }}>
        {data && data.buckets.length > 0 ? (
          <ResponsiveContainer>
            <LineChart data={data.buckets} margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
              <CartesianGrid stroke="#E7E5E4" strokeDasharray="3 3" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10, fill: "#57534E", fontWeight: 700 }}
                interval={hours === 24 ? 2 : Math.floor(hours / 12)}
              />
              <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: "#57534E", fontWeight: 700 }} />
              <Tooltip
                contentStyle={{ border: "2px solid #1C1917", boxShadow: "3px 3px 0 0 #1C1917", borderRadius: 0, fontSize: 12 }}
                labelStyle={{ color: "#1C1917", fontWeight: 900 }}
              />
              <Legend wrapperStyle={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }} />
              <Line type="monotone" dataKey="visits" stroke="#1C1917" strokeWidth={2.5} name="Visitas" dot={false} />
              <Line type="monotone" dataKey="unique_sessions" stroke="#7C3AED" strokeWidth={2.5} name="Únicas" dot={false} />
              <Line type="monotone" dataKey="new_users" stroke="#E5484D" strokeWidth={2.5} name="Nuevos usuarios" dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-full grid place-items-center text-[#57534E] text-sm">Cargando…</div>
        )}
      </div>
    </div>
  );
}

// ============ LIVE TAB ============
function LiveTab() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/overview");
      setData(data);
      setError(null);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 10000); // refresh every 10s
    return () => clearInterval(id);
  }, [load]);

  if (error) return <div className="text-[#E5484D] font-bold">{error}</div>;
  if (!data) return <div className="text-[#57534E] eyebrow">Cargando…</div>;

  const t = data.totals || {};

  return (
    <div className="space-y-8" data-testid="live-tab">
      {/* Live counts hero */}
      <div className="border-2 border-[#1C1917] bg-[#1C1917] text-[#FDFBF7] p-8 shadow-[6px_6px_0_0_#46A758]" data-testid="live-hero">
        <div className="grid sm:grid-cols-3 gap-6 items-center">
          <div>
            <div className="flex items-center gap-3">
              <span className="relative flex h-3 w-3">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#46A758] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-3 w-3 bg-[#46A758]"></span>
              </span>
              <span className="eyebrow text-xs text-[#FFC53D]">EN VIVO AHORA</span>
            </div>
            <div className="display text-7xl mt-3" data-testid="live-count">{data.live_count}</div>
            <div className="text-xs text-[#A8A29E] uppercase tracking-wider mt-1">
              {data.live_authenticated} registrados · {data.live_anonymous} anónimos
            </div>
          </div>
          <div className="sm:col-span-2">
            <span className="eyebrow text-xs text-[#FFC53D]">PAÍSES</span>
            <div className="mt-3 flex flex-wrap gap-2" data-testid="live-countries">
              {data.live_countries.length === 0 && (
                <span className="text-xs text-[#A8A29E]">Sin visitantes activos.</span>
              )}
              {data.live_countries.map((c) => (
                <div
                  key={c.code}
                  className="border-2 border-[#FDFBF7] px-3 py-2 flex items-center gap-2 text-sm"
                >
                  <span className="text-lg">{flag(c.code)}</span>
                  <strong>{c.code}</strong>
                  <span className="text-[#A8A29E]">· {c.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Live sessions table */}
      <div className="border-2 border-[#1C1917] bg-white shadow-[3px_3px_0_0_#1C1917]" data-testid="live-sessions">
        <div className="border-b-2 border-[#1C1917] px-5 py-3 flex items-center justify-between">
          <span className="eyebrow text-xs">Sesiones activas</span>
          <span className="text-[10px] text-[#57534E] uppercase tracking-wider">Actualiza cada 10s</span>
        </div>
        <div className="divide-y-2 divide-[#1C1917]">
          {data.live_sessions.length === 0 ? (
            <div className="p-6 text-center text-[#57534E] text-sm">No hay nadie ahora mismo.</div>
          ) : (
            data.live_sessions.map((s) => (
              <div key={s.session_id} className="px-5 py-3 grid grid-cols-12 gap-3 items-center text-sm" data-testid={`session-${s.session_id}`}>
                <div className="col-span-1 text-2xl">{flag(s.country_code)}</div>
                <div className="col-span-3 truncate">
                  <div className="font-bold text-[#1C1917]">{s.country || "—"}</div>
                  <div className="text-xs text-[#57534E]">{s.city || "—"}</div>
                </div>
                <div className="col-span-5 truncate">
                  {s.is_authenticated ? (
                    <div>
                      <span className="inline-block bg-[#46A758] text-white px-2 py-0.5 text-[10px] font-black tracking-wider uppercase">REG</span>
                      <span className="ml-2 text-[#1C1917]">{s.user_email}</span>
                    </div>
                  ) : (
                    <span className="text-xs text-[#57534E] uppercase tracking-wider font-bold">Anónimo</span>
                  )}
                  <div className="text-xs text-[#57534E] font-mono mt-1 truncate">{s.path}</div>
                </div>
                <div className="col-span-3 text-right text-xs text-[#57534E] uppercase tracking-wider font-bold">
                  hace {timeAgo(s.last_seen)}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Traffic chart */}
      <TrafficChart />

      {/* Totals */}
      <div>
        <span className="eyebrow text-xs">Resumen</span>
        <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Usuarios totales" value={t.users || 0} testId="stat-users" />
          <StatCard label="Nuevos hoy" value={t.new_users_today || 0} color="#7C3AED" testId="stat-new-today" />
          <StatCard label="En prueba" value={t.trialing || 0} color="#FFC53D" testId="stat-trialing" />
          <StatCard label="Suscripciones activas" value={t.active || 0} color="#46A758" testId="stat-active" />
          <StatCard label="Plan Mensual" value={t.monthly_subs || 0} testId="stat-monthly" />
          <StatCard label="Plan Anual" value={t.annual_subs || 0} color="#7C3AED" testId="stat-annual" />
          <StatCard label="Visitas 24 h" value={t.visits_24h || 0} testId="stat-visits-24" />
          <StatCard label="Visitas 7 d" value={t.visits_7d || 0} testId="stat-visits-7" />
        </div>
        {(t.pending_payments || 0) > 0 && (
          <div className="mt-4 border-2 border-[#1C1917] bg-[#FFC53D] p-4 flex items-center justify-between shadow-[3px_3px_0_0_#1C1917]">
            <div>
              <span className="eyebrow text-xs">Atención</span>
              <p className="text-sm font-bold text-[#1C1917] mt-1">
                Tienes {t.pending_payments} solicitud{t.pending_payments === 1 ? "" : "es"} de pago pendiente{t.pending_payments === 1 ? "" : "s"}.
              </p>
            </div>
            <Link
              to="/admin/payments"
              className="bg-[#1C1917] text-[#FDFBF7] px-4 py-2 font-black text-xs tracking-widest uppercase border-2 border-[#1C1917] hover:bg-black"
              data-testid="btn-go-payments"
            >
              Revisar →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

// ============ USERS TAB ============
const STATUS_LABELS = {
  trialing: { label: "Prueba", bg: "#FFC53D", color: "#1C1917" },
  active: { label: "Activa", bg: "#46A758", color: "#FFFFFF" },
  expired: { label: "Expirada", bg: "#E5484D", color: "#FFFFFF" },
  admin: { label: "Admin", bg: "#1C1917", color: "#FFC53D" },
};
const FILTERS = [
  { id: "", label: "Todos" },
  { id: "trialing", label: "En prueba" },
  { id: "active", label: "Activos" },
  { id: "expired", label: "Expirados" },
];

// ---- Modal: Crear usuario ----
function CreateUserModal({ onClose, onCreated }) {
  const [form, setForm] = useState({
    email: "",
    name: "",
    password: "",
    role: "user",
    grant_plan: "",
  });
  const [saving, setSaving] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim() || !form.name.trim() || form.password.length < 8) {
      toast.error("Completa email, nombre y una contraseña de 8+ caracteres.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        email: form.email.trim().toLowerCase(),
        name: form.name.trim(),
        password: form.password,
        role: form.role,
      };
      if (form.grant_plan) payload.grant_plan = form.grant_plan;
      await api.post("/admin/users", payload);
      toast.success("Usuario creado");
      onCreated();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-[#1C1917]/70 grid place-items-center p-4"
      onClick={onClose}
      data-testid="create-user-modal"
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-[#FDFBF7] border-2 border-[#1C1917] shadow-[6px_6px_0_0_#1C1917]"
      >
        <div className="border-b-2 border-[#1C1917] px-6 py-4 flex items-center justify-between bg-[#FFC53D]">
          <span className="display text-xl text-[#1C1917]">Crear usuario</span>
          <button
            type="button"
            onClick={onClose}
            className="text-[#1C1917] font-black text-xl leading-none w-8 h-8 grid place-items-center hover:bg-[#1C1917] hover:text-[#FFC53D] border-2 border-[#1C1917]"
            data-testid="create-user-close"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div>
            <label className="eyebrow text-[10px]">Email</label>
            <input
              type="email"
              required
              value={form.email}
              onChange={(e) => update("email", e.target.value)}
              className="mt-1 w-full h-11 px-3 border-2 border-[#1C1917] bg-white focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917]"
              data-testid="create-user-email"
            />
          </div>
          <div>
            <label className="eyebrow text-[10px]">Nombre</label>
            <input
              type="text"
              required
              value={form.name}
              onChange={(e) => update("name", e.target.value)}
              className="mt-1 w-full h-11 px-3 border-2 border-[#1C1917] bg-white focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917]"
              data-testid="create-user-name"
            />
          </div>
          <div>
            <label className="eyebrow text-[10px]">Contraseña (8+ caracteres)</label>
            <input
              type="text"
              required
              minLength={8}
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              className="mt-1 w-full h-11 px-3 border-2 border-[#1C1917] bg-white font-mono focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917]"
              data-testid="create-user-password"
              placeholder="Generar y copiar para enviar al cliente"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="eyebrow text-[10px]">Rol</label>
              <select
                value={form.role}
                onChange={(e) => update("role", e.target.value)}
                className="mt-1 w-full h-11 px-3 border-2 border-[#1C1917] bg-white focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917]"
                data-testid="create-user-role"
              >
                <option value="user">Usuario</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div>
              <label className="eyebrow text-[10px]">Acceso inicial</label>
              <select
                value={form.grant_plan}
                onChange={(e) => update("grant_plan", e.target.value)}
                className="mt-1 w-full h-11 px-3 border-2 border-[#1C1917] bg-white focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917]"
                data-testid="create-user-plan"
              >
                <option value="">Prueba 7 días (defecto)</option>
                <option value="trial_30">Prueba 30 días</option>
                <option value="monthly">Plan Mensual (30 d)</option>
                <option value="annual">Plan Anual (365 d)</option>
              </select>
            </div>
          </div>
        </div>
        <div className="border-t-2 border-[#1C1917] px-6 py-4 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="h-11 px-4 border-2 border-[#1C1917] bg-white font-black text-xs tracking-widest uppercase hover:bg-[#FFC53D]"
            data-testid="create-user-cancel"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={saving}
            className="h-11 px-5 border-2 border-[#1C1917] bg-[#1C1917] text-[#FDFBF7] font-black text-xs tracking-widest uppercase shadow-[3px_3px_0_0_#FFC53D] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#FFC53D] disabled:opacity-50"
            data-testid="create-user-submit"
          >
            {saving ? "Creando…" : "Crear usuario"}
          </button>
        </div>
      </form>
    </div>
  );
}

// ---- Modal: Acciones sobre usuario ----
function UserActionsModal({ user, onClose, onChanged }) {
  const [busy, setBusy] = useState(null); // action key being executed
  const [days, setDays] = useState(7);
  const [newPassword, setNewPassword] = useState("");

  const post = async (action, body = {}) => {
    setBusy(action);
    try {
      await api.post(`/admin/users/${user.id}/action`, { action, ...body });
      toast.success("Acción aplicada");
      onChanged();
      if (action === "delete") onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(null);
    }
  };

  const confirmDelete = () => {
    if (window.confirm(`¿Eliminar definitivamente a ${user.email}?\n\nEsta acción no se puede deshacer.`)) {
      post("delete");
    }
  };

  const submitReset = (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      toast.error("Mínimo 8 caracteres");
      return;
    }
    post("reset_password", { new_password: newPassword });
    setNewPassword("");
  };

  const isAdmin = user.role === "admin" || user.effective_status === "admin";

  return (
    <div
      className="fixed inset-0 z-50 bg-[#1C1917]/70 grid place-items-center p-4"
      onClick={onClose}
      data-testid="user-actions-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-[#FDFBF7] border-2 border-[#1C1917] shadow-[6px_6px_0_0_#1C1917] max-h-[90vh] overflow-y-auto"
      >
        <div className="border-b-2 border-[#1C1917] px-6 py-4 flex items-center justify-between bg-[#7C3AED] text-white">
          <div>
            <span className="eyebrow text-[10px] text-[#FFC53D]">Gestionar</span>
            <div className="display text-xl mt-0.5">{user.name || user.email}</div>
            <div className="text-xs opacity-80">{user.email}</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-white font-black text-xl leading-none w-8 h-8 grid place-items-center hover:bg-white hover:text-[#7C3AED] border-2 border-white"
            data-testid="user-actions-close"
            aria-label="Cerrar"
          >
            ×
          </button>
        </div>

        <div className="p-6 space-y-6">
          {/* Activar plan */}
          <section>
            <span className="eyebrow text-[10px]">Acceso y plan</span>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-2">
              <button
                onClick={() => post("activate_monthly")}
                disabled={busy !== null}
                className="h-11 border-2 border-[#1C1917] bg-[#46A758] text-white font-black text-xs tracking-widest uppercase shadow-[3px_3px_0_0_#1C1917] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#1C1917] disabled:opacity-50"
                data-testid="action-activate-monthly"
              >
                {busy === "activate_monthly" ? "…" : "Activar mensual"}
              </button>
              <button
                onClick={() => post("activate_annual")}
                disabled={busy !== null}
                className="h-11 border-2 border-[#1C1917] bg-[#46A758] text-white font-black text-xs tracking-widest uppercase shadow-[3px_3px_0_0_#1C1917] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#1C1917] disabled:opacity-50"
                data-testid="action-activate-annual"
              >
                {busy === "activate_annual" ? "…" : "Activar anual"}
              </button>
              <button
                onClick={() => post("expire")}
                disabled={busy !== null}
                className="h-11 border-2 border-[#1C1917] bg-[#E5484D] text-white font-black text-xs tracking-widest uppercase shadow-[3px_3px_0_0_#1C1917] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#1C1917] disabled:opacity-50"
                data-testid="action-expire"
              >
                {busy === "expire" ? "…" : "Expirar acceso"}
              </button>
            </div>
          </section>

          {/* Extender prueba */}
          <section className="border-2 border-[#1C1917] p-4 bg-white">
            <span className="eyebrow text-[10px]">Extender prueba</span>
            <div className="mt-2 flex items-center gap-2">
              <input
                type="number"
                min={1}
                max={365}
                value={days}
                onChange={(e) => setDays(parseInt(e.target.value, 10) || 1)}
                className="h-11 w-24 px-3 border-2 border-[#1C1917] bg-white text-center font-bold focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917]"
                data-testid="action-extend-days"
              />
              <span className="text-xs uppercase font-bold tracking-wider">días desde ahora</span>
              <button
                onClick={() => post("extend_trial", { days })}
                disabled={busy !== null}
                className="h-11 px-4 border-2 border-[#1C1917] bg-[#FFC53D] font-black text-xs tracking-widest uppercase shadow-[3px_3px_0_0_#1C1917] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#1C1917] disabled:opacity-50 ml-auto"
                data-testid="action-extend-trial"
              >
                {busy === "extend_trial" ? "…" : "Aplicar"}
              </button>
            </div>
          </section>

          {/* Rol */}
          <section>
            <span className="eyebrow text-[10px]">Rol</span>
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => post("set_admin")}
                disabled={busy !== null || isAdmin}
                className="h-11 px-4 border-2 border-[#1C1917] bg-[#1C1917] text-[#FFC53D] font-black text-xs tracking-widest uppercase hover:bg-black disabled:opacity-50"
                data-testid="action-set-admin"
              >
                Hacer admin
              </button>
              <button
                onClick={() => post("set_user")}
                disabled={busy !== null || !isAdmin}
                className="h-11 px-4 border-2 border-[#1C1917] bg-white font-black text-xs tracking-widest uppercase hover:bg-[#FFC53D] disabled:opacity-50"
                data-testid="action-set-user"
              >
                Quitar admin
              </button>
            </div>
          </section>

          {/* Reset contraseña */}
          <form onSubmit={submitReset} className="border-2 border-[#1C1917] p-4 bg-white">
            <span className="eyebrow text-[10px]">Restablecer contraseña</span>
            <div className="mt-2 flex items-center gap-2">
              <input
                type="text"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Nueva contraseña (8+ caracteres)"
                className="h-11 flex-1 px-3 border-2 border-[#1C1917] bg-white font-mono focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917]"
                data-testid="action-new-password"
              />
              <button
                type="submit"
                disabled={busy !== null}
                className="h-11 px-4 border-2 border-[#1C1917] bg-[#7C3AED] text-white font-black text-xs tracking-widest uppercase shadow-[3px_3px_0_0_#1C1917] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#1C1917] disabled:opacity-50"
                data-testid="action-reset-password"
              >
                {busy === "reset_password" ? "…" : "Cambiar"}
              </button>
            </div>
          </form>

          {/* Eliminar */}
          <section className="border-2 border-[#E5484D] p-4 bg-[#FFF5F5]">
            <span className="eyebrow text-[10px] text-[#E5484D]">Zona peligrosa</span>
            <div className="mt-2 flex items-center justify-between gap-3">
              <p className="text-xs text-[#57534E] font-bold">
                Eliminar el usuario y todos sus pagos / perfiles asociados.
              </p>
              <button
                onClick={confirmDelete}
                disabled={busy !== null}
                className="h-11 px-4 border-2 border-[#1C1917] bg-[#E5484D] text-white font-black text-xs tracking-widest uppercase shadow-[3px_3px_0_0_#1C1917] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#1C1917] disabled:opacity-50"
                data-testid="action-delete"
              >
                {busy === "delete" ? "…" : "Eliminar"}
              </button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function UsersTab() {
  const [filter, setFilter] = useState("");
  const [q, setQ] = useState("");
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [actionUser, setActionUser] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filter) params.set("status", filter);
      if (q.trim()) params.set("q", q.trim());
      const { data } = await api.get(`/admin/users?${params.toString()}`);
      setUsers(data.users || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [filter, q]);

  useEffect(() => { load(); }, [load]);

  // Keep the open actions modal in sync with refreshed user data.
  useEffect(() => {
    if (!actionUser) return;
    const fresh = users.find((u) => u.id === actionUser.id);
    if (fresh && fresh !== actionUser) setActionUser(fresh);
    else if (!fresh) setActionUser(null);
  }, [users, actionUser]);

  return (
    <div className="space-y-6" data-testid="users-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Buscar por email o nombre…"
          className="h-11 px-3 border-2 border-[#1C1917] bg-white text-[#1C1917] placeholder:text-[#A8A29E] focus:outline-none focus:shadow-[3px_3px_0_0_#1C1917] transition-shadow w-64"
          data-testid="users-search"
        />
        <div className="inline-flex border-2 border-[#1C1917] shadow-[3px_3px_0_0_#1C1917]" data-testid="users-filters">
          {FILTERS.map((f, i) => (
            <button
              key={f.id || "all"}
              onClick={() => setFilter(f.id)}
              className={`px-4 h-11 font-black text-xs tracking-wider uppercase ${i > 0 ? "border-l-2 border-[#1C1917]" : ""} ${
                filter === f.id ? "bg-[#1C1917] text-[#FDFBF7]" : "bg-white text-[#1C1917] hover:bg-[#FFC53D]"
              }`}
              data-testid={`users-filter-${f.id || "all"}`}
            >
              {f.label}
            </button>
          ))}
        </div>
        <span className="text-xs text-[#57534E] uppercase tracking-wider font-bold">
          {loading ? "Cargando…" : `${users.length} usuarios`}
        </span>
        <button
          onClick={() => setShowCreate(true)}
          className="ml-auto h-11 px-5 border-2 border-[#1C1917] bg-[#FFC53D] text-[#1C1917] font-black text-xs tracking-widest uppercase shadow-[3px_3px_0_0_#1C1917] hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-[2px_2px_0_0_#1C1917]"
          data-testid="btn-create-user"
        >
          + Crear usuario
        </button>
      </div>

      <div className="border-2 border-[#1C1917] bg-white shadow-[3px_3px_0_0_#1C1917]">
        <div className="border-b-2 border-[#1C1917] px-5 py-3 hidden sm:grid grid-cols-12 gap-3 text-[10px] font-black tracking-widest uppercase text-[#57534E]">
          <div className="col-span-3">Usuario</div>
          <div className="col-span-2">Estado</div>
          <div className="col-span-2">Plan</div>
          <div className="col-span-2">Vence</div>
          <div className="col-span-2">Registrado</div>
          <div className="col-span-1 text-right">Acción</div>
        </div>
        <div className="divide-y-2 divide-[#1C1917]">
          {users.length === 0 ? (
            <div className="p-6 text-center text-[#57534E] text-sm">Sin usuarios para este filtro.</div>
          ) : (
            users.map((u) => {
              const s = STATUS_LABELS[u.effective_status] || STATUS_LABELS.trialing;
              return (
                <div key={u.id} className="px-5 py-3 grid sm:grid-cols-12 gap-3 items-center text-sm" data-testid={`user-row-${u.id}`}>
                  <div className="sm:col-span-3 truncate">
                    <div className="font-bold text-[#1C1917] flex items-center gap-2">
                      {u.name || u.email}
                      {u.role === "admin" && (
                        <span className="inline-block px-1.5 py-0.5 text-[9px] font-black tracking-widest uppercase bg-[#1C1917] text-[#FFC53D] border border-[#1C1917]">
                          ADMIN
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-[#57534E] truncate">{u.email}</div>
                  </div>
                  <div className="sm:col-span-2">
                    <span
                      className="inline-block px-2 py-1 text-[10px] font-black tracking-widest uppercase border-2 border-[#1C1917]"
                      style={{ background: s.bg, color: s.color }}
                    >
                      {s.label}
                    </span>
                  </div>
                  <div className="sm:col-span-2 text-xs text-[#1C1917] uppercase tracking-wider font-bold">
                    {u.plan_type === "annual" ? "Anual" : u.plan_type === "monthly" ? "Mensual" : "—"}
                  </div>
                  <div className="sm:col-span-2 text-xs text-[#57534E]">
                    {u.effective_status === "active"
                      ? formatDate(u.active_until)
                      : u.effective_status === "trialing"
                      ? formatDate(u.trial_end_at)
                      : "—"}
                  </div>
                  <div className="sm:col-span-2 text-xs text-[#57534E]">
                    {formatDate(u.created_at)}
                  </div>
                  <div className="sm:col-span-1 sm:text-right">
                    <button
                      onClick={() => setActionUser(u)}
                      className="h-9 px-3 border-2 border-[#1C1917] bg-white font-black text-[10px] tracking-widest uppercase hover:bg-[#FFC53D]"
                      data-testid={`btn-manage-${u.id}`}
                    >
                      Gestionar
                    </button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {showCreate && (
        <CreateUserModal
          onClose={() => setShowCreate(false)}
          onCreated={load}
        />
      )}
      {actionUser && (
        <UserActionsModal
          user={actionUser}
          onClose={() => setActionUser(null)}
          onChanged={load}
        />
      )}
    </div>
  );
}

// ============ ROOT ADMIN PAGE ============
const TABS = [
  { to: "/admin", label: "En vivo", end: true, testId: "tab-live" },
  { to: "/admin/users", label: "Usuarios", testId: "tab-users" },
  { to: "/admin/payments", label: "Pagos", testId: "tab-payments" },
];

export default function AdminLayout() {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/dashboard" replace />;

  // For nested routes (e.g. /admin/users) we render Outlet via React Router.
  // Here we render manually based on path to keep this file self-contained.
  const path = location.pathname;
  return (
    <div className="min-h-[calc(100vh-4rem)] bg-[#FDFBF7]" data-testid="admin-layout">
      <div className="max-w-6xl mx-auto px-6 md:px-10 py-12">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <span className="eyebrow text-xs">Panel administrador</span>
            <h1 className="hero-display text-4xl sm:text-5xl text-[#1C1917] mt-2">DocuConvert</h1>
          </div>
          <div className="text-xs text-[#57534E] uppercase tracking-widest font-bold">
            {user.email}
          </div>
        </div>

        <div className="mt-8 inline-flex border-2 border-[#1C1917] shadow-[3px_3px_0_0_#1C1917] flex-wrap" data-testid="admin-tabs">
          {TABS.map((tab, i) => (
            <NavLink
              key={tab.to}
              to={tab.to}
              end={tab.end}
              className={({ isActive }) =>
                `px-5 h-11 inline-flex items-center font-black text-xs tracking-wider uppercase transition-colors ${
                  i > 0 ? "border-l-2 border-[#1C1917]" : ""
                } ${isActive ? "bg-[#1C1917] text-[#FDFBF7]" : "bg-white text-[#1C1917] hover:bg-[#FFC53D]"}`
              }
              data-testid={tab.testId}
            >
              {tab.label}
            </NavLink>
          ))}
        </div>

        <div className="mt-8">
          {path === "/admin" && <LiveTab />}
          {path === "/admin/users" && <UsersTab />}
          {path === "/admin/payments" && <AdminPaymentsTab embedded />}
        </div>
      </div>
    </div>
  );
}
